import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

import { SqlAggregateFunctionType } from '@models/helpers';
import { TableFieldValueType } from '@models/module/fields/main';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { CipoControlComponent } from '@shared/components/fields/cipo-control/cipo-control.component';
import { CipoOutlineFieldComponent } from '@shared/components/fields/cipo-outline-field/cipo-outline-field.component';
import { CipoFieldConfig, CipoFormGroup, CipoTableConfig } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { ConfirmationDialog } from '@shared/dialogs/confirmation-dialog.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TableFieldConfig, TableFieldService } from './common';

@Component({
  selector: 'cipo-table-field',
  imports: [
    MatTableModule,
    DragDropModule,
    MatIconModule,
    CipoControlComponent,
    CipoOutlineFieldComponent,
    FlexLayoutModule,
    MatMenuModule,
    MatButtonModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './table-field.component.html',
  styleUrl: './table-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableFieldComponent extends CipoControlBase<TableFieldValueType[], CipoTableConfig> {
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private tableFieldsService = inject(TableFieldService);
  protected table = viewChild(MatTable);
  protected cdr = inject(ChangeDetectorRef);

  tableFieldId = input<number, string>(null, { transform: id => Number(id) });

  /** for now is fine as signal, evaluate in future to move to config, if needed elsewhere, currently used by weather */
  loading = signal(false);

  /** the purpose is to signal an external data is loading and block table actions, used by weather when the service is loading data */
  externalDataLoading = signal(false);

  tableFields = signal<TableFieldConfig[]>(null);

  tableFormArray = linkedSignal(() => {
    this.deepFieldData.readonly(); // to trigger update when readonly changes
    return this.createFormArray();
  });

  hasFooter = computed(() => Object.keys(this.fieldData()?.aggregateValue || {}).length > 0);
  footerValuesDict = computed(() => {
    if (!this.hasFooter()) return {};
    const dict: Record<string, { aggregateFunctionType: string; aggregateValue: string }> = {};
    this.tableFields()?.forEach(field => {
      const aggregate = this.fieldData().aggregateValue?.[this.columnsDictionary()[field.key].id];
      if (aggregate) {
        dict[field.key] = {
          aggregateFunctionType: SqlAggregateFunctionType[field.aggregateId],
          aggregateValue: aggregate,
        };
      }
    });
    return dict;
  });
  defaultTableFieldsData = computed(() => {
    const fieldsData: Record<string, CipoFieldConfig> = {};
    const fields = this.tableFields();
    const tableFieldEditable = this.deepFieldData.readonly() === false;
    fields.forEach(field => {
      const { label, fieldDescription, ...fieldData } = field;
      fieldData.cleanReadOnly = true;
      fieldData.readonly = true;
      /* Remove all the lists when a table becomes editable. This will ensure that cipo-select / cipo-radio-group load all options */
      if (tableFieldEditable && field.options?.length) {
        fieldData.options = undefined;
      }
      fieldData.position = {
        x: 0,
        y: 0,
        cols: 1,
        rows: 1,
      };
      fieldsData[field.key] = fieldData;
    });
    return fieldsData;
  });
  tableFieldsData = computed(() => {
    const fieldsData: Record<string, CipoFieldConfig>[] = [];
    const defaultFields = this.defaultTableFieldsData();
    const activeRowIndex = this.activeRowIndex();
    /* This is actually the tableField value */
    const value = this.control()?.value ?? [];
    /* if active row index is equal to the length of the value array, add a new row */
    if (activeRowIndex === value?.length) {
      const tableFields = untracked(this.tableFields);
      const defaultValues: Record<string, any> = {};
      tableFields.forEach(field => {
        defaultValues[field.key] = field.value || null;
      });
      value.push(defaultValues);
    }
    if (!defaultFields || !value?.length) {
      return [];
    }
    value.forEach((row, index) => {
      const rowData = structuredClone(defaultFields);
      if (this.activeRowIndex() === index) {
        for (const key in rowData) {
          rowData[key].readonly = false;
        }
      }
      fieldsData.push(rowData);
    });
    return fieldsData;
  });
  totalWidth = computed(() => this.tableFields()?.reduce((acc, field) => acc + (field.width || 1), 0) || 0);
  columnWidths = computed(() => {
    const totalWidth = this.totalWidth();
    const widths = {};
    this.tableFields()?.forEach(field => {
      widths[field.key] = totalWidth ? (100 / totalWidth) * (field.width || 1) + '%' : 'auto';
    });
    return widths;
  });
  columnsDictionary = computed(() => {
    const columns: Record<string, { label: string; id: number }> = {};
    this.tableFields().forEach(({ label, id, key }) => {
      columns[key] = { label: label ?? key, id };
    });
    return columns;
  });
  tableColumns = linkedSignal(() => {
    const cols = this.tableFields()?.map(({ key }) => key);
    if (!cols?.length) return [];
    const readonly = this.deepFieldData.readonly();
    if (this.deepFieldData.showPosition() || !readonly) {
      cols.unshift('position');
    }
    if (readonly) {
      return cols;
    }
    cols.push('action');
    return cols;
  });
  tableFooterColumns = computed(() => {
    const readonly = this.deepFieldData.readonly();
    if (!readonly) return [];
    return this.tableColumns();
  });
  activeRowIndex = signal<number | null>(null);
  timeFieldSubscription: Subscription;

  constructor() {
    super();
    effect(() => {
      const formArray = this.tableFormArray();
      if (!formArray) return;
      this.timeFieldSubscription?.unsubscribe();

      /* Subscribe to form array value changes, and update the table control value */
      this.timeFieldSubscription = formArray.valueChanges.pipe(debounceTime(DEBOUNCE_TIMES.default)).subscribe(() => {
        this.control()?.setValue(formArray.getRawValue() as any);
        this.control()?.markAsDirty();
        this.control()?.updateValueAndValidity();
        this.cdr.markForCheck();
      });
    });

    effect(() => {
      if (this.deepFieldData.readonly()) {
        this.activeRowIndex.set(null);
      }
    });

    effect(() => {
      // mark for check to update the table when the control value is set from outside of this component
      // this is needed when we mark the control as touched programatically from outside, because we need to show the validation errors in the table
      const touched = this.controlTouched();
      if (touched) {
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit() {
    this.loadFields();
  }

  loadFields() {
    const { entityInstanceId, isDraft } = this.fieldData();
    this.tableFieldsService
      .getTableFields({
        id: this.tableFieldId(),
        entityInstanceId,
        viewMode: this.fieldData().readonly,
        value: this.control().value,
        isDraft,
      })
      .subscribe(fields => {
        this.tableFields.set(fields);
      });
  }

  createFormArray() {
    const tableFields = this.tableFields();
    if (!tableFields?.length) return null;
    const value = this.control()?.value ?? [];
    const groups: CipoFormGroup[] = [];

    untracked(() => {
      if (Array.isArray(value) && value.length) {
        /* Populate array with values. label: null because we have labels as the table header */
        value.forEach(groupVal => groups.push(this.createFormRow(tableFields, groupVal)));
      }
    });
    return this.fb.array(groups);
  }

  createFormRow(tableFields: TableFieldConfig[], value?: TableFieldValueType) {
    const formGroup = this.fb.group({}) as CipoFormGroup;
    tableFields.forEach(field => {
      formGroup.addControl(field.key, new FormControl(value ? value?.[field.key] : field.value));
    });
    return formGroup;
  }

  drop(event: CdkDragDrop<string>) {
    moveItemInArray(this.tableFormArray().controls, event.previousIndex, event.currentIndex);
    const currentActiveIndex = this.activeRowIndex();

    if (currentActiveIndex !== null) {
      if (event.previousIndex === currentActiveIndex) {
        this.activeRowIndex.set(event.currentIndex);
      } else if (event.previousIndex < currentActiveIndex && event.currentIndex >= currentActiveIndex) {
        this.activeRowIndex.set(currentActiveIndex - 1);
      } else if (event.previousIndex > currentActiveIndex && event.currentIndex <= currentActiveIndex) {
        this.activeRowIndex.set(currentActiveIndex + 1);
      }
    }
    this.control()?.setValue(this.tableFormArray().getRawValue() as any);
    this.control()?.markAsDirty();
    this.table().renderRows();
  }

  deleteRow(index: number) {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'cipoFields.deleteRow',
          description: 'cipoFields.deleteMessage',
        },
      })
      .afterClosed()
      .pipe(filter(val => !!val))
      .subscribe(() => this.deleteRowComplete(index));
  }

  deleteRowComplete(index: number) {
    this.tableFormArray().removeAt(index);
    this.table().renderRows();
    this.activeRowIndex.update(i => {
      if (i < index) return i;
      if (i > index) return i - 1;
      return null;
    });
  }

  insertRow() {
    const value = this.control()?.value ?? [];
    const formRowValue = this.createFormRow(this.tableFields()).getRawValue();
    this.control()?.setValue([...value, formRowValue as any]);
    // Recreate formArray after updated the value
    this.tableFormArray.set(this.createFormArray());
    this.activeRowIndex.set(this.tableFormArray().length - 1);
    this.table().renderRows();
  }

  editRow(index: number) {
    const formArray = this.tableFormArray();
    const activeRowIndex = this.activeRowIndex();
    if (activeRowIndex !== null) {
      const activeForm = formArray.at(activeRowIndex);
      activeForm.markAllAsTouched();
      activeForm.updateValueAndValidity();
      if (!activeForm.valid) return;
    }
    this.activeRowIndex.set(index);
  }
}
